// Routine create/edit screen. Single component for both modes (controlled by
// the route param :id === 'new' vs an existing cuid). Mirrors the legacy
// routine-editor.html: name + folder + ordered exercise list with per-row
// targets, plus an exercise-picker modal.
import type { Exercise, RoutineExercise, Units } from '@onflow/shared';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ExercisePicker } from '../components/ExercisePicker.js';
import { EmptyState } from '../components/v2/EmptyState.js';
import { FlowButton } from '../components/v2/FlowButton.js';
import { FlowCard, FlowLabel } from '../components/v2/FlowCard.js';
import { FlowInput, FlowSegment } from '../components/v2/FlowInput.js';
import { Icon } from '../components/v2/Icon.js';
import { PageHeader } from '../components/v2/PageHeader.js';
import { useMe, useUpdateProfile } from '../hooks/useAuth.js';
import { useExercises } from '../hooks/useExercises.js';
import {
  useCreateRoutine,
  useRoutine,
  useRoutines,
  useUpdateRoutine,
} from '../hooks/useRoutines.js';
import { ApiRequestError } from '../lib/api.js';
import { kgToUnit, unitLabel, unitToKg } from '../lib/format.js';
import { toast } from '../lib/toast.js';

function blankExercise(exerciseId: string): RoutineExercise {
  return {
    exerciseId,
    targetSets: 3,
    targetReps: 10,
    targetWeight: 0,
    restTimerSeconds: 90,
    notes: '',
  };
}

/**
 * Numeric field that keeps its own text state while focused so the user can
 * type freely — clear the box, enter a decimal like "0.5", edit mid-string —
 * without the controlled number value snapping back on every keystroke. A bare
 * `value={number}` + `Number(e.target.value) || 0` binding fights the caret and
 * silently drops partial input (a trailing "." parses to the old number), which
 * left these boxes only adjustable via the spin arrows. Commits the parsed
 * number on each valid change; normalises to `fallback` on blur when emptied.
 */
function NumberField({
  label,
  value,
  min,
  max,
  step,
  fallback,
  onValueChange,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  fallback: number;
  onValueChange: (n: number) => void;
}) {
  const [text, setText] = useState(String(value));
  const [focused, setFocused] = useState(false);

  // Re-sync from the model when it changes externally (e.g. loading an existing
  // routine) but never while the user is mid-edit.
  useEffect(() => {
    if (!focused) setText(String(value));
  }, [value, focused]);

  return (
    <FlowInput
      label={label}
      type="number"
      inputMode="decimal"
      min={min}
      max={max}
      step={step}
      value={text}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        const n = text.trim() === '' ? fallback : Number(text);
        const safe = Number.isFinite(n) ? n : fallback;
        setText(String(safe));
        onValueChange(safe);
      }}
      onChange={(e) => {
        const t = e.target.value;
        setText(t);
        if (t.trim() !== '') {
          const n = Number(t);
          if (Number.isFinite(n)) onValueChange(n);
        }
      }}
    />
  );
}

export function RoutineEditor() {
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isNew = !routeId || routeId === 'new';
  const editingId = isNew ? undefined : routeId;

  const existing = useRoutine(editingId);
  const allRoutines = useRoutines(); // for folder suggestions
  const exercises = useExercises();
  const me = useMe();
  const updateProfile = useUpdateProfile();
  const units: Units = me.data?.profile.units ?? 'KG';
  const createMutation = useCreateRoutine();
  const updateMutation = useUpdateRoutine(editingId ?? '');

  const [name, setName] = useState('');
  // On a new routine, a `?folder=` param pre-selects the split this day belongs
  // to — set by the "Add day to <folder>" buttons on the routines/workout
  // screens so a PPL folder accumulates push/pull/legs without retyping.
  const [folder, setFolder] = useState(isNew ? (searchParams.get('folder') ?? '') : '');
  const [items, setItems] = useState<RoutineExercise[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Hydrate the form from the server response when editing an existing
  // routine. Runs once per fetched routine.
  useEffect(() => {
    if (existing.data) {
      setName(existing.data.name);
      setFolder(existing.data.folder ?? '');
      setItems(existing.data.exercises);
    }
  }, [existing.data]);

  const exerciseById = useMemo(() => {
    const map = new Map<string, Exercise>();
    for (const ex of exercises.data ?? []) map.set(ex.id, ex);
    return map;
  }, [exercises.data]);

  const folderSuggestions = useMemo(() => {
    const set = new Set<string>();
    for (const r of allRoutines.data ?? []) if (r.folder) set.add(r.folder);
    return Array.from(set).sort();
  }, [allRoutines.data]);

  const moveItem = (idx: number, delta: number) => {
    setItems((prev) => {
      const next = [...prev];
      const target = idx + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target]!, next[idx]!];
      return next;
    });
  };

  const removeItem = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateItem = (idx: number, patch: Partial<RoutineExercise>) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const handlePick = (ex: Exercise) => {
    setItems((prev) => [...prev, blankExercise(ex.id)]);
    setPickerOpen(false);
  };

  const save = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Please give your routine a name.');
      return;
    }
    if (items.length === 0) {
      setError('Add at least one exercise.');
      return;
    }
    const payload = {
      name: name.trim(),
      folder: folder.trim() ? folder.trim() : null,
      exercises: items,
    };
    try {
      if (isNew) {
        await createMutation.mutateAsync(payload);
        toast.success('Routine created');
      } else {
        await updateMutation.mutateAsync(payload);
        toast.success('Routine updated');
      }
      navigate('/routines');
    } catch (err) {
      // 422s come back with field errors; surface a concise message inline so
      // the user doesn't have to dig into the toast.
      if (err instanceof ApiRequestError && err.fieldErrors?.length) {
        setError(err.fieldErrors.map((f) => `${f.field}: ${f.message ?? f.code}`).join('; '));
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Save failed.');
      }
    }
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  if (!isNew && existing.isLoading) {
    return (
      <section className="fade-in">
        <EmptyState title="Loading routine…" />
      </section>
    );
  }

  if (!isNew && existing.error) {
    return (
      <section className="fade-in">
        <EmptyState
          tone="error"
          role="alert"
          title="Couldn't load routine"
          body={(existing.error as Error).message}
        />
      </section>
    );
  }

  const iconBtnStyle = {
    display: 'grid' as const,
    placeItems: 'center' as const,
    width: 30,
    height: 30,
    borderRadius: 'var(--r-xs)',
    background: 'var(--paper-2)',
    border: '1px solid var(--edge)',
    color: 'var(--ink-3)',
    cursor: 'pointer',
    fontSize: 14,
  };

  return (
    <section className="fade-in">
      <PageHeader
        eyebrow="Train"
        title={isNew ? 'New Routine' : 'Edit Routine'}
        action={
          <FlowButton variant="ghost" size="sm" onClick={() => navigate('/routines')}>
            Cancel
          </FlowButton>
        }
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <FlowInput
          id="routine-name"
          label="Routine name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Push Day, Leg Blast…"
          maxLength={60}
        />

        <div>
          <FlowInput
            id="routine-folder"
            label="Split folder (optional)"
            hint="Group days under one split, e.g. PPL holding Push, Pull, Legs."
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
            placeholder="e.g. PPL, Upper/Lower…"
            list="routine-folder-suggestions"
            maxLength={120}
          />
          <datalist id="routine-folder-suggestions">
            {folderSuggestions.map((f) => (
              <option key={f} value={f} />
            ))}
          </datalist>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          margin: '22px 0 10px',
        }}
      >
        <FlowLabel style={{ margin: 0 }}>Exercises ({items.length})</FlowLabel>
        <FlowSegment
          aria-label="Weight unit"
          items={[
            { id: 'KG', label: 'kg' },
            { id: 'LB', label: 'lb' },
          ]}
          active={units}
          onSelect={(id) => updateProfile.mutate({ units: id as Units })}
          style={{ width: 108 }}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((item, idx) => {
          const meta = exerciseById.get(item.exerciseId);
          return (
            <FlowCard key={`${item.exerciseId}-${idx}`} padding="sm">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink-1)', minWidth: 0 }}>
                  <span style={{ color: 'var(--ink-3)' }}>#{idx + 1}</span>{' '}
                  {meta?.name ?? 'Unknown exercise'}
                </div>
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  <button
                    type="button"
                    style={{ ...iconBtnStyle, opacity: idx === 0 ? 0.4 : 1 }}
                    onClick={() => moveItem(idx, -1)}
                    disabled={idx === 0}
                    aria-label="Move up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    style={{ ...iconBtnStyle, opacity: idx === items.length - 1 ? 0.4 : 1 }}
                    onClick={() => moveItem(idx, 1)}
                    disabled={idx === items.length - 1}
                    aria-label="Move down"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    style={iconBtnStyle}
                    onClick={() => removeItem(idx)}
                    aria-label="Remove exercise"
                  >
                    <Icon name="trash" size={14} />
                  </button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                <NumberField
                  label="Sets"
                  min={1}
                  max={50}
                  fallback={1}
                  value={item.targetSets}
                  onValueChange={(n) => updateItem(idx, { targetSets: n })}
                />
                <NumberField
                  label="Reps"
                  min={1}
                  max={100}
                  fallback={1}
                  value={item.targetReps}
                  onValueChange={(n) => updateItem(idx, { targetReps: n })}
                />
                <NumberField
                  label={`Weight (${unitLabel(units)})`}
                  min={0}
                  step={0.5}
                  fallback={0}
                  value={kgToUnit(item.targetWeight, units)}
                  onValueChange={(n) => updateItem(idx, { targetWeight: unitToKg(n, units) })}
                />
                <NumberField
                  label="Rest (s)"
                  min={0}
                  max={3600}
                  step={15}
                  fallback={0}
                  value={item.restTimerSeconds}
                  onValueChange={(n) => updateItem(idx, { restTimerSeconds: n })}
                />
              </div>
            </FlowCard>
          );
        })}
      </div>

      <div style={{ marginTop: 12 }}>
        <FlowButton variant="soft" block onClick={() => setPickerOpen(true)}>
          + Add exercise
        </FlowButton>
      </div>

      {items.length === 0 && (
        <p style={{ marginTop: 16, fontSize: 13, color: 'var(--ink-2)', textAlign: 'center' }}>
          No exercises yet. Tap &quot;Add exercise&quot; to begin.
        </p>
      )}

      {error && (
        <div role="alert" style={{ marginTop: 16, fontSize: 14, color: 'var(--off-track)' }}>
          {error}
        </div>
      )}

      <div style={{ marginTop: 24, marginBottom: 40 }}>
        <FlowButton variant="primary" block disabled={saving} onClick={() => void save()}>
          {saving ? 'Saving…' : isNew ? 'Save routine' : 'Save changes'}
        </FlowButton>
      </div>

      <ExercisePicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={handlePick} />
    </section>
  );
}
